import React, { useEffect, useCallback } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { AhuType } from "../../../models/unitType";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import useTheme from "@mui/material/styles/useTheme";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";

const Heater = ({ heatingTypes }) => {
  const { control, watch, setValue, trigger } = useFormContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const baseUnitType = params?.unitType ? parseInt(params.unitType, 10) : null;

  // Watch form values
  const unitType = watch("Type");
  const heaterType = watch("heaterType");
  const heaterAirInputParam = watch("heaterAirInputParam");
  const heaterWaterInputParam = watch("heaterWaterInputParam");
  const airTempInHeat = watch("airTempInHeat") || false;
  const heatPumpTemperatureAfterWinter = watch(
    "heatPumpTemperatureAfterWinter",
  );
  const heatPumpHumidityAfterWinter = watch("heatPumpHumidityAfterWinter");
  const summerOutdoorTemperature = watch("summerOutdoorTemperature");
  const summerOutdoorHumidity = watch("summerOutdoorHumidity");

  // Check if Energy:Prime Genius mode
  const isPrimeGenius =
    baseUnitType === 2 && unitType === AhuType.ENERGY_PRIME_GENIUS;

  // Check if Line:Prime mode
  const isLinePrime = baseUnitType === AhuType.LINE_PRIME;

  // Pull values from heat pump output for Prime Genius (only when toggle is OFF)
  useEffect(() => {
    if (
      isPrimeGenius &&
      !airTempInHeat &&
      heatPumpTemperatureAfterWinter != null
    ) {
      setValue("coilInputTemperatureHeater", heatPumpTemperatureAfterWinter, {
        shouldValidate: false,
      });
    }
  }, [
    isPrimeGenius,
    airTempInHeat,
    heatPumpTemperatureAfterWinter,
    heatPumpHumidityAfterWinter,
    setValue,
  ]);

  useEffect(() => {
    if (
      isPrimeGenius &&
      !airTempInHeat &&
      heatPumpHumidityAfterWinter != null
    ) {
      setValue("coilInputHumidityHeater", heatPumpHumidityAfterWinter, {
        shouldValidate: false,
      });
    }
  }, [isPrimeGenius, airTempInHeat, heatPumpHumidityAfterWinter, setValue]);

  // Pull values from Operating Point Summer section for Line:Prime
  // (only when toggle is OFF, mirroring the Prime Genius pattern)
  useEffect(() => {
    if (
      isLinePrime &&
      !airTempInHeat &&
      summerOutdoorTemperature != null &&
      summerOutdoorTemperature !== ""
    ) {
      setValue("coilInputTemperatureHeater", summerOutdoorTemperature, {
        shouldValidate: false,
      });
    }
  }, [isLinePrime, airTempInHeat, summerOutdoorTemperature, setValue]);

  useEffect(() => {
    if (
      isLinePrime &&
      !airTempInHeat &&
      summerOutdoorHumidity != null &&
      summerOutdoorHumidity !== ""
    ) {
      setValue("coilInputHumidityHeater", summerOutdoorHumidity, {
        shouldValidate: false,
      });
    }
  }, [isLinePrime, airTempInHeat, summerOutdoorHumidity, setValue]);

  // Validation rules
  const validationRules = {
    coilInputTemperatureHeater: {
      required: t("validation.required") || "Required",
    },
    coilInputHumidityHeater: {
      required: t("validation.required") || "Required",
    },
    heaterAirInputParamValue: {
      required: t("validation.required") || "Required",
    },
    heaterType: {
      required: t("validation.required") || "Required",
    },
    heaterConcentration: {
      required: t("validation.required") || "Required",
      max: {
        value: 35,
        message: "Concentration must be between 0 and 35",
      },
    },
    heaterTemperatureIn: {
      required: t("validation.required") || "Required",
    },
    heaterWaterInputParamValue: {
      required: t("validation.required") || "Required",
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: isPrimeGenius
          ? { xl: "100%", xs: "100%" }
          : { xl: "50%", xs: "100%" },
      }}
    >
      <Box
        sx={{
          width: { xl: "100%", xs: "100%" },
          mt: { sm: 1, xl: 0 },
          ml: { sm: 0, xl: 1 },
          mb: { sm: 0, xl: 1 },
        }}
      >
        <Typography sx={{ m: "16px", fontSize: "1.4rem" }}>
          {t("coils.heater")}
        </Typography>

        {/* Air data section */}
        <Box sx={{ mt: { xl: 1, sm: 1 } }}>
          <Typography sx={{ m: "16px" }}>
            {t("coils.title") || "Air data"}
          </Typography>
          <Controller
            name="airTempInHeat"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value || false}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                }
                label={t("coils.manualChange")}
              />
            )}
          />
          <Box sx={{ mb: { sm: 1, xl: 1 } }}>
            <Controller
              name="coilInputTemperatureHeater"
              control={control}
              rules={validationRules.coilInputTemperatureHeater}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  disabled={!airTempInHeat}
                  id="coilInputTemperatureHeater"
                  label={t("coils.airTemperatureIn")}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">°C</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                  inputProps={{
                    step: 0.1,
                  }}
                />
              )}
            />
          </Box>
          <Box sx={{ mb: { sm: 1, xl: 1 } }}>
            <Controller
              name="coilInputHumidityHeater"
              control={control}
              rules={validationRules.coilInputHumidityHeater}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  disabled={!airTempInHeat}
                  id="coilInputHumidityHeater"
                  label={t("coils.relativeHumidityIn")}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                  inputProps={{
                    step: 0.1,
                  }}
                />
              )}
            />
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Controller
          name="heaterAirInputParam"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel id="airInputParams">
                {t("coils.airInputParameter")}
              </InputLabel>
              <Select
                {...field}
                labelId="airInputParams"
                id="airInputParams"
                label={t("coils.airInputParameter")}
                MenuProps={{
                  disableScrollLock: true,
                }}
              >
                <MenuItem value="Supply Temperature">
                  {t("coils.supplyCoilTemperature")}
                </MenuItem>
                <MenuItem value="Capacity">{t("coils.capacity")}</MenuItem>
              </Select>
            </FormControl>
          )}
        />

        <Box sx={{ mt: { sm: 1, xl: 1 } }}>
          <Controller
            name="heaterAirInputParamValue"
            control={control}
            rules={validationRules.heaterAirInputParamValue}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                id="winterSupplyTemperature"
                label={
                  heaterAirInputParam === "Capacity"
                    ? t("coils.capacity")
                    : t("coils.supplyCoilTemperature")
                }
                type="number"
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      {heaterAirInputParam === "Supply Temperature"
                        ? "°C"
                        : "kW"}
                    </InputAdornment>
                  ),
                  onWheel: (e) => e.target.blur(),
                }}
                inputProps={{
                  step: 0.1,
                }}
              />
            )}
          />
        </Box>

        <Box sx={{ mt: { xl: 1, sm: 1 } }}>
          <Typography sx={{ m: "16px" }}>
            {t("operatingpoint.mediumData")}
          </Typography>
          <Controller
            name="heaterType"
            control={control}
            rules={validationRules.heaterType}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <InputLabel id="heaterType-label">
                  {t("operatingpoint.heaterType")}
                </InputLabel>
                <Select
                  {...field}
                  sx={{
                    width: { xl: "100%", xs: "100%" },
                    display: "block",
                    overflow: "hidden",
                  }}
                  labelId="heaterType-label"
                  id="heaterType"
                  label={t("operatingpoint.heaterType")}
                  MenuProps={{
                    disableScrollLock: true,
                  }}
                >
                  {heatingTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Box>
        <Box
          sx={{
            display: { xl: "flex", xs: "block", gap: "0%" },
            backgroundColor: theme.palette.primary.light,
          }}
        >
          <Box
            sx={{
              width: { xl: "50%", xs: "100%" },
              mt: { sm: 1, xl: 1 },
              mr: { sm: 1, xl: 1 },
            }}
          >
            <Controller
              name="heaterConcentration"
              control={control}
              rules={validationRules.heaterConcentration}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="concentration"
                  label={t("operatingpoint.concentration")}
                  disabled={heaterType === "water"}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    trigger("heaterConcentration");
                  }}
                  inputProps={{
                    step: 1,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              )}
            />
          </Box>
          <Box
            sx={{
              width: { xl: "50%", xs: "100%" },
              mt: { sm: 1, xl: 1 },
            }}
          >
            <Controller
              name="heaterWaterInputParam"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="waterInputParamsLabel">
                    {t("coils.waterInputParameter")}
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="waterInputParamsLabel"
                    id="waterInputParams"
                    label={t("coils.waterInputParameter")}
                    MenuProps={{
                      disableScrollLock: true,
                    }}
                  >
                    <MenuItem value="Temperature out">
                      {t("coils.temperatureOut")}
                    </MenuItem>
                    <MenuItem value="Fluid flow">
                      {t("coils.fluidFlow")}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: { xl: "flex", xs: "block" },
            backgroundColor: theme.palette.primary.light,
          }}
        >
          <Box
            sx={{
              width: { xl: "50%", xs: "100%" },
              mt: { sm: 1, xl: 1 },
              mr: { sm: 1, xl: 1 },
            }}
          >
            <Controller
              name="heaterTemperatureIn"
              control={control}
              rules={validationRules.heaterTemperatureIn}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="temperatureIn"
                  label={t("operatingpoint.temperatureIn")}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{
                    step: 1,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">°C</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              width: { xl: "50%", xs: "100%" },
              mt: { sm: 1, xl: 1 },
            }}
          >
            <Controller
              name="heaterWaterInputParamValue"
              control={control}
              rules={validationRules.heaterWaterInputParamValue}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="waterInputParamValueHeater"
                  label={
                    heaterWaterInputParam === "Temperature out"
                      ? t("operatingpoint.temperatureOut")
                      : t("operatingpoint.fluidFlow")
                  }
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{
                    step: 1,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        {heaterWaterInputParam === "Temperature out"
                          ? "°C"
                          : "m³/h"}
                      </InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const Cooler = ({ heatingTypes }) => {
  const { control, watch, setValue, trigger } = useFormContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const baseUnitType = params?.unitType ? parseInt(params.unitType, 10) : null;

  // Watch form values
  const coolerType = watch("coolerType");
  const coolerAirInputParam = watch("coolerAirInputParam");
  const coolerWaterInputParam = watch("coolerWaterInputParam");
  const airTempInCool = watch("airTempInCool") || false;
  const summerOutdoorTemperature = watch("summerOutdoorTemperature");
  const summerOutdoorHumidity = watch("summerOutdoorHumidity");

  // Check if Line:Prime mode
  const isLinePrime = baseUnitType === AhuType.LINE_PRIME;

  // Pull values from Operating Point Summer section for Line:Prime
  // (only when toggle is OFF, mirroring the Heater behavior)
  useEffect(() => {
    if (
      isLinePrime &&
      !airTempInCool &&
      summerOutdoorTemperature != null &&
      summerOutdoorTemperature !== ""
    ) {
      setValue("coilInputTemperatureCooler", summerOutdoorTemperature, {
        shouldValidate: false,
      });
    }
  }, [isLinePrime, airTempInCool, summerOutdoorTemperature, setValue]);

  useEffect(() => {
    if (
      isLinePrime &&
      !airTempInCool &&
      summerOutdoorHumidity != null &&
      summerOutdoorHumidity !== ""
    ) {
      setValue("coilInputHumidityCooler", summerOutdoorHumidity, {
        shouldValidate: false,
      });
    }
  }, [isLinePrime, airTempInCool, summerOutdoorHumidity, setValue]);

  // Validation rules
  const validationRules = {
    coilInputTemperatureCooler: {
      required: t("validation.required") || "Required",
    },
    coilInputHumidityCooler: {
      required: t("validation.required") || "Required",
    },
    coolerAirInputParamValue: {
      required: t("validation.required") || "Required",
    },
    coolerType: {
      required: t("validation.required") || "Required",
    },
    coolerConcentration: {
      required: t("validation.required") || "Required",
      max: {
        value: 35,
        message: "Concentration must be between 0 and 35",
      },
    },
    coolerTemperatureIn: {
      required: t("validation.required") || "Required",
    },
    coolerWaterInputParamValue: {
      required: t("validation.required") || "Required",
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: { xl: "50%", xs: "100%" },
      }}
    >
      <Box
        sx={{
          width: { xl: "100%", xs: "100%" },
          mt: { sm: 1, xl: 0 },
          ml: { sm: 0, xl: 1 },
          mb: { sm: 0, xl: 1 },
        }}
      >
        <Typography sx={{ m: "16px", fontSize: "1.4rem" }}>
          {t("coils.cooler")}
        </Typography>
        <Typography sx={{ m: "16px" }}>{t("coils.title")}</Typography>
        <Controller
          name="airTempInCool"
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={value || false}
                  onChange={(e) => onChange(e.target.checked)}
                />
              }
              label={t("coils.manualChange")}
            />
          )}
        />
        <Box sx={{ mb: { xl: 1, sm: 1 } }}>
          <Controller
            name="coilInputTemperatureCooler"
            control={control}
            rules={validationRules.coilInputTemperatureCooler}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                disabled={!airTempInCool}
                id="CoilInputTemperatureCooler"
                label={t("coils.airTemperatureIn")}
                type="number"
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">°C</InputAdornment>
                  ),
                  onWheel: (e) => e.target.blur(),
                }}
                inputProps={{
                  step: 0.1,
                }}
              />
            )}
          />
        </Box>
        <Box sx={{ mb: { xl: 1, sm: 1 } }}>
          <Controller
            name="coilInputHumidityCooler"
            control={control}
            rules={validationRules.coilInputHumidityCooler}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                disabled={!airTempInCool}
                id="coilInputHumidityCooler"
                label={t("coils.relativeHumidityIn")}
                type="number"
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  ),
                  onWheel: (e) => e.target.blur(),
                }}
                inputProps={{
                  step: 0.1,
                }}
              />
            )}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Controller
          name="coolerAirInputParam"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel id="airInputParams-label">
                {t("coils.airInputParameter")}
              </InputLabel>
              <Select
                {...field}
                labelId="airInputParams-label"
                id="airInputParams"
                label={t("coils.airInputParameter")}
                MenuProps={{
                  disableScrollLock: true,
                }}
              >
                <MenuItem value="Supply Temperature">
                  {t("coils.supplyCoilTemperature")}
                </MenuItem>
                <MenuItem value="Capacity">{t("coils.capacity")}</MenuItem>
              </Select>
            </FormControl>
          )}
        />
        <Box sx={{ mt: { xl: 1, sm: 1 } }}>
          <Controller
            name="coolerAirInputParamValue"
            control={control}
            rules={validationRules.coolerAirInputParamValue}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                id="summerSupplyTemperature"
                label={
                  coolerAirInputParam === "Capacity"
                    ? t("coils.capacity")
                    : t("coils.supplyCoilTemperature")
                }
                type="number"
                InputLabelProps={{ shrink: true }}
                error={!!error}
                helperText={error?.message}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      {coolerAirInputParam === "Supply Temperature"
                        ? "°C"
                        : "kW"}
                    </InputAdornment>
                  ),
                  onWheel: (e) => e.target.blur(),
                }}
                inputProps={{
                  step: 0.1,
                }}
              />
            )}
          />
        </Box>
        <Box sx={{ mt: { xl: 1, sm: 1 } }}>
          <Typography sx={{ m: "16px" }}>
            {t("operatingpoint.mediumData")}
          </Typography>
          <Controller
            name="coolerType"
            control={control}
            rules={validationRules.coolerType}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <InputLabel id="coolerType-label">
                  {t("operatingpoint.heaterType")}
                </InputLabel>
                <Select
                  {...field}
                  sx={{
                    width: { xl: "100%", xs: "100%" },
                    display: "block",
                    overflow: "hidden",
                  }}
                  labelId="coolerType-label"
                  id="coolerType"
                  label={t("operatingpoint.heaterType")}
                  MenuProps={{
                    disableScrollLock: true,
                  }}
                >
                  {heatingTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Box>
        <Box
          sx={{
            display: { xl: "flex", xs: "block" },
            backgroundColor: theme.palette.primary.light,
          }}
        >
          <Box
            sx={{
              width: { xl: "50%", xs: "100%" },
              mt: { sm: 1, xl: 1 },
              mr: { sm: 1, xl: 1 },
            }}
          >
            <Controller
              name="coolerConcentration"
              control={control}
              rules={validationRules.coolerConcentration}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="concentration"
                  label={t("operatingpoint.concentration")}
                  disabled={coolerType === "water"}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{
                    step: 1,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">%</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    trigger("coolerConcentration");
                  }}
                />
              )}
            />
          </Box>
          <Box
            sx={{
              width: { xl: "50%", xs: "100%" },
              mt: { sm: 1, xl: 1 },
            }}
          >
            <Controller
              name="coolerWaterInputParam"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>{t("coils.waterInputParameter")}</InputLabel>
                  <Select
                    {...field}
                    labelId="coolerWaterInputParam-label"
                    id="coolerWaterInputParam"
                    label={t("coils.waterInputParameter")}
                    MenuProps={{
                      disableScrollLock: true,
                    }}
                  >
                    <MenuItem value="Temperature out">
                      {t("coils.temperatureOut")}
                    </MenuItem>
                    <MenuItem value="Fluid flow">
                      {t("coils.fluidFlow")}
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: { xl: "flex", xs: "block" },
            backgroundColor: theme.palette.primary.light,
          }}
        >
          <Box
            sx={{
              width: { xl: "50%", xs: "100%" },
              mt: { sm: 1, xl: 1 },
              mr: { sm: 1, xl: 1 },
            }}
          >
            <Controller
              name="coolerTemperatureIn"
              control={control}
              rules={validationRules.coolerTemperatureIn}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="temperatureIn"
                  label={t("operatingpoint.temperatureIn")}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{
                    step: 1,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">°C</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              width: { xl: "50%", xs: "100%" },
              mt: { sm: 1, xl: 1 },
            }}
          >
            <Controller
              name="coolerWaterInputParamValue"
              control={control}
              rules={validationRules.coolerWaterInputParamValue}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  id="waterInputParamValueCooler"
                  label={
                    coolerWaterInputParam === "Temperature out"
                      ? t("coils.temperatureOut")
                      : t("coils.fluidFlow")
                  }
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                  inputProps={{
                    step: 1,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        {coolerWaterInputParam === "Temperature out"
                          ? "°C"
                          : "m³/h"}
                      </InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              )}
            />
          </Box>
        </Box>
        {isLinePrime && (
          <Controller
            name="hasDropEliminator"
            control={control}
            defaultValue={false}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                  />
                }
                label="Drop eliminator?"
              />
            )}
          />
        )}
      </Box>
    </Paper>
  );
};

function Coils() {
  const { watch, setValue } = useFormContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const baseUnitType = params?.unitType ? parseInt(params.unitType, 10) : null;

  // Watch form values
  const coils = watch("coils");
  const unitType = watch("Type");
  const heaterAirInputParam = watch("heaterAirInputParam");
  const heaterWaterInputParam = watch("heaterWaterInputParam");
  const coolerAirInputParam = watch("coolerAirInputParam");

  // Remove Propylene Glycol from all Medium Type options (applies to all unit types)
  const heatingTypes = [
    { value: "water", label: t("operatingpoint.heatingType.water") },
    { value: "ethylene", label: t("operatingpoint.heatingType.ethylene") },
  ];

  // Helper function to check if Energy:Prime Standard defaults should be applied
  const shouldApplyEnergyPrimeStandardDefaults = useCallback(() => {
    return (
      baseUnitType === 2 &&
      unitType === AhuType.ENERGY_PRIME_STANDARD &&
      coils === "both"
    );
  }, [baseUnitType, unitType, coils]);

  // Helper function to check if Energy:Prime Genius mode
  const isPrimeGenius = useCallback(() => {
    return baseUnitType === 2 && unitType === AhuType.ENERGY_PRIME_GENIUS;
  }, [baseUnitType, unitType]);

  // Apply default values for Energy:Prime Standard
  useEffect(() => {
    if (!shouldApplyEnergyPrimeStandardDefaults()) {
      return;
    }

    if (coolerAirInputParam === "Supply Temperature") {
      setValue("coolerAirInputParamValue", "16", { shouldValidate: false });
    }

    if (heaterAirInputParam === "Supply Temperature") {
      setValue("heaterAirInputParamValue", "23", { shouldValidate: false });
    }

    setValue("heaterTemperatureIn", "45", { shouldValidate: false });

    if (heaterWaterInputParam === "Temperature out") {
      setValue("heaterWaterInputParamValue", "40", { shouldValidate: false });
    }
  }, [
    shouldApplyEnergyPrimeStandardDefaults,
    coolerAirInputParam,
    heaterAirInputParam,
    heaterWaterInputParam,
    setValue,
  ]);

  // Apply default values for Energy:Prime Genius
  useEffect(() => {
    if (!isPrimeGenius()) {
      return;
    }

    if (heaterAirInputParam === "Supply Temperature") {
      setValue("heaterAirInputParamValue", "27", { shouldValidate: false });
    }

    setValue("heaterTemperatureIn", "45", { shouldValidate: false });

    if (heaterWaterInputParam === "Temperature out") {
      setValue("heaterWaterInputParamValue", "40", { shouldValidate: false });
    }
  }, [isPrimeGenius, heaterAirInputParam, heaterWaterInputParam, setValue]);

  // Check if Prime Genius mode (only show Heater)
  const isPrimeGeniusMode = isPrimeGenius();

  return (
    <Paper sx={{ p: { xs: 1, md: 5 } }}>
      <Box style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
        {/* For Prime Genius, only show Heater */}
        {isPrimeGeniusMode && <Heater heatingTypes={heatingTypes} />}
        {!isPrimeGeniusMode && coils === "heater" && (
          <Heater heatingTypes={heatingTypes} />
        )}
        {!isPrimeGeniusMode && coils === "cooler" && (
          <Cooler heatingTypes={heatingTypes} />
        )}
        {!isPrimeGeniusMode && coils === "both" && (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Heater heatingTypes={heatingTypes} />
              <Cooler heatingTypes={heatingTypes} />
            </Box>
          </>
        )}
        {!isPrimeGeniusMode &&
          coils !== "heater" &&
          coils !== "cooler" &&
          coils !== "both" && (
            <div style={{ textAlign: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Typography
                  sx={{ textAlign: "center", fontSize: "38px", mb: "12px" }}
                >
                  {t("coils.noCoils")}
                </Typography>
              </Box>

              <Typography variant="h5">{t("coils.question1")}</Typography>
              <Typography variant="h6">{t("coils.question2")}</Typography>

              <Button
                onClick={() => {
                  // Navigate to Type of Unit step - this would need to be handled by parent
                  // For now, we'll just log it
                  console.log("Navigate to Type of Unit step");
                }}
                sx={{
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.secondary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.secondary.main,
                  },
                  my: "2em",
                }}
              >
                {t("coils.addCoils")}
              </Button>
              <Typography variant="h6">{t("coils.question3")}</Typography>
            </div>
          )}
      </Box>
    </Paper>
  );
}

export default Coils;
